/**
 * Tính toán màu THUẦN cho lớp giao diện: trộn màu, đặt màu trong suốt lên nền, tỉ lệ tương phản theo
 * WCAG 2.1. Dùng khi dựng bảng màu (`theme-tokens.ts`) và trong bài test tương phản của nó.
 */
export type Rgba = readonly [red: number, green: number, blue: number, alpha: number];

/** Đọc «#rgb», «#rrggbb», «rgb(…)», «rgba(…)». Chuỗi lạ → ném lỗi: bảng màu sai thì phải biết ngay. */
export function parseColor(value: string): Rgba {
  const text = value.trim();
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(text);
  if (short) {
    const [, r = '0', g = '0', b = '0'] = short;
    return [parseInt(r + r, 16), parseInt(g + g, 16), parseInt(b + b, 16), 1];
  }
  const long = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})?$/i.exec(text);
  if (long) {
    const [, r = '00', g = '00', b = '00', a] = long;
    return [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16), a ? parseInt(a, 16) / 255 : 1];
  }
  const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/i.exec(
    text,
  );
  if (rgb) return [Number(rgb[1]), Number(rgb[2]), Number(rgb[3]), rgb[4] ? Number(rgb[4]) : 1];
  throw new Error(`Không đọc được màu «${value}»`);
}

const channelHex = (channel: number) =>
  Math.round(Math.min(255, Math.max(0, channel)))
    .toString(16)
    .padStart(2, '0');

export const toHex = ([red, green, blue]: Rgba | readonly [number, number, number]) =>
  `#${channelHex(red)}${channelHex(green)}${channelHex(blue)}`;

/** Trộn `color` vào `into`: `share` = 1 là nguyên `color`, 0 là nguyên `into`. Bỏ qua độ trong. */
export function mix(color: string, into: string, share: number): string {
  const a = parseColor(color);
  const b = parseColor(into);
  return toHex([
    a[0] * share + b[0] * (1 - share),
    a[1] * share + b[1] * (1 - share),
    a[2] * share + b[2] * (1 - share),
  ]);
}

/** Màu đen/trắng… với độ trong — chữ của Ant Design là «đen 88%», «trắng 65%»… */
export function withAlpha(color: string, alpha: number): string {
  const [red, green, blue] = parseColor(color);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

/** Màu THẤY ĐƯỢC khi đặt `foreground` (có thể trong suốt) lên nền đặc `background`. */
export function flatten(foreground: string, background: string): string {
  const top = parseColor(foreground);
  const base = parseColor(background);
  const alpha = top[3];
  return toHex([
    top[0] * alpha + base[0] * (1 - alpha),
    top[1] * alpha + base[1] * (1 - alpha),
    top[2] * alpha + base[2] * (1 - alpha),
  ]);
}

export function relativeLuminance(color: string): number {
  const linear = (channel: number) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  const [red, green, blue] = parseColor(color);
  return 0.2126 * linear(red) + 0.7152 * linear(green) + 0.0722 * linear(blue);
}

/**
 * Tỉ lệ tương phản WCAG của `foreground` đặt trên nền đặc `background` (1 → 21). Chữ thường cần ≥ 4,5;
 * chữ lớn và hình không phải chữ (icon, viền ô) cần ≥ 3.
 */
export function contrastRatio(foreground: string, background: string): number {
  const a = relativeLuminance(flatten(foreground, background));
  const b = relativeLuminance(background);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}
